// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { TabDefinition } from "@insite/client-framework/Types/TabDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";

import { LinkFieldValue, Option } from "@insite/client-framework/Types/FieldDefinition";
import { LazyImageProps } from "@insite/mobius/LazyImage";

import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LazyImage from "@store/MobiusOverrides/LazyImage/LazyImage";
import useCurrentBreakpointIndex from "@store/Framework/Common/Hooks/useCurrentBreakpointIndex";
import {
    createResponsiveFieldDefinitions,
    mergeResponsiveFields,
    ResponsiveTabConfig,
    WithResponsiveFields,
} from "@store/Framework/Common/Utilities/FieldDefinitionBuilder/responsiveFieldsHelpers";
import { getContextualCssUnitField } from "@store/Framework/Common/Utilities/FieldHelpers";
import { useGetLink } from "@store/Framework/Store/Links/LinksSelectors";
import { FoundationFieldDefinition } from "@store/Framework/Types/FoundationFieldDefinition";
import CardComponent from "@store/Library/Widgets/Card/Card";
import CardBodyComponent from "@store/Library/Widgets/Card/CardBody";
import * as cssLinter from "css";
import React from "react";
import { css } from "styled-components";

enum fields {
    link = "link",
    logo = "logo",
    image = "image",
    bodyheight = "bodyheight",
    bodyWidth = "bodyWidth",
    backgroundColor = "backgroundColor",
    objectFit = "objectFit",
    shadow = "shadow",
    radius = "radius",
    logoHorizontalPadding = "logoHorizontalPadding",
    logoVerticalPadding = "logoVerticalPadding",
    logoheight = "logoheight",
    logowidth = "logowidth",
    imageHorizontalPadding = "imageHorizontalPadding",
    imageVerticalPadding = "imageVerticalPadding",
    imageHeight = "imageHeight",
    imageWidth = "imageWidth",
    blendMode = "blendMode",
    customCSS = "customCSS",
}
type TabNames = "Basic" | "Logo" | "Image";

interface BasicFields {
    [fields.bodyheight]: string;
    [fields.bodyWidth]: string;
    [fields.shadow]: ShadowOptions;
    [fields.radius]: string;
    [fields.link]: LinkFieldValue;
}

interface LogoFields {
    [fields.logo]: string;
    [fields.logoHorizontalPadding]: string;
    [fields.logoVerticalPadding]: string;
    [fields.logowidth]: string;
    [fields.logoheight]: string;
}

interface ImageFields {
    [fields.image]: string;
    [fields.backgroundColor]: string;
    [fields.objectFit]: string;
    [fields.imageHorizontalPadding]: string;
    [fields.imageVerticalPadding]: string;
    [fields.imageHeight]: string;
    [fields.imageWidth]: string;
    [fields.blendMode]: string;
}

type LinkContainerFields = BasicFields & LogoFields & ImageFields;
type LinkContainerProps = WidgetProps & {
    fields: WithResponsiveFields<LinkContainerFields, TabNames> & {
        [fields.customCSS]: string;
    };
};

const defaultCustomCss = `& {
}

.card {
}

.card-body {
}

.logo-image {
}

.logo {
}

.card-footer {
}`;

interface LinkContainerStyles {
    container?: InjectableCss;
    logo?: LazyImageProps;
    body?: InjectableCss;
    logoContainer?: InjectableCss;
}

const styles: LinkContainerStyles = {
    container: {
        css: css`
            padding: 0;
            position: relative;
        `,
    },
    body: {
        css: css`
            padding: 0;
            position: relative;
        `,
    },
    logoContainer: {
        css: css`
            position: absolute;
        `,
    },
};

const LinkContainer: React.FC<LinkContainerProps> = ({ id, fields, fields: { customCSS } }) => {
    const currentBreakpoint = useCurrentBreakpointIndex() ?? 0;
    const { isInShell } = useShellContext();
    const currentBreakPointStyles = mergeResponsiveFields(
        fields,
        ["responsiveBasicFields", "responsiveLogoFields", "responsiveImageFields"],
        currentBreakpoint,
    );
    const {
        backgroundColor,
        blendMode,
        bodyWidth,
        bodyheight,
        image,
        imageHeight,
        imageHorizontalPadding,
        imageVerticalPadding,
        imageWidth,
        link,
        logo,
        logoHorizontalPadding,
        logoVerticalPadding,
        logoheight,
        logowidth,
        objectFit,
        radius,
        shadow,
    } = currentBreakPointStyles;
    const isManualObjectFit = objectFit === "manual";
    const linkUrl = useGetLink(link).url;

    const containerStyles: InjectableCss = {
        css: css`
            ${styles.container?.css}
            background-color: ${backgroundColor};
            width: ${bodyWidth};
            border-radius: ${radius};
            height: ${bodyheight};
            ${({ theme }) => `box-shadow: ${shadow ? theme.shadows[shadow] : `none`};`}
            :hover {
                box-shadow: ${({ theme }) => theme.shadows.small};
            }
        `,
    };
    const bodyStyles: InjectableCss = {
        css: css`
            ${styles.body?.css}
            background-image: url(${image});
            background-size: ${isManualObjectFit ? `${imageWidth} ${imageHeight}` : objectFit};
            width: ${bodyWidth};
            height: ${bodyheight};
            background-position: ${isManualObjectFit ? `${imageHorizontalPadding} ${imageVerticalPadding}` : "center"};
            background-repeat: no-repeat;
            mix-blend-mode: ${blendMode};
        `,
    };

    const logoContainerStyles: InjectableCss = {
        css: css`
            ${styles.logoContainer?.css}
            left: calc(2.5% + ${logoHorizontalPadding || "0px"});
            top: calc(2.5% + ${logoVerticalPadding || "0px"});
            position: absolute;
        `,
    };
    const logoStyles: LazyImageProps = {
        src: logo,
        altText: logo,
        css: css`
            ${styles.logo?.css}
            width: ${logowidth};
            height: ${logoheight};
        `,
    };

    const customCssWrapper = {
        css: css`
            ${customCSS}
        `,
    };
    if (currentBreakpoint === null) {
        return null;
    }
    return (
        <StyledWrapper {...customCssWrapper}>
            <CardComponent
                href={isInShell ? undefined : linkUrl}
                extendedStyles={containerStyles}
                variant="unstyled"
                className="card"
            >
                <CardBodyComponent extendedStyles={bodyStyles} className="card-body">
                    <StyledWrapper {...logoContainerStyles} className="logo">
                        <LazyImage {...logoStyles} className="logo-image" />
                    </StyledWrapper>
                </CardBodyComponent>
                <StyledWrapper className="card-footer">
                    <Zone contentId={id} zoneName="footer" />
                </StyledWrapper>
            </CardComponent>
        </StyledWrapper>
    );
};

const verticalPaddingField = getContextualCssUnitField(fields.logoVerticalPadding).definition;
const horizontalPaddingField = getContextualCssUnitField(fields.logoHorizontalPadding).definition;
const radiusField = getContextualCssUnitField(fields.radius).definition;
const bodyHeightField = getContextualCssUnitField(fields.bodyheight).definition;
const bodyWidthField = getContextualCssUnitField(fields.bodyWidth).definition;
const logoWidthField = getContextualCssUnitField(fields.logowidth).definition;
const logoHeightField = getContextualCssUnitField(fields.logoheight).definition;
const imageHorizontalPaddingField = getContextualCssUnitField(fields.imageHorizontalPadding).definition;
const imageVerticalPaddingField = getContextualCssUnitField(fields.imageVerticalPadding).definition;
const imageHeightField = getContextualCssUnitField(fields.imageHeight).definition;
const imageWidthField = getContextualCssUnitField(fields.imageWidth).definition;

const imageTab: TabDefinition = {
    displayName: "Image",
    sortOrder: 1,
};
type ShadowOptions = "small" | "main" | "alt" | "2" | "";

const shadowOptions: Option<ShadowOptions>[] = [
    { displayName: "None", value: "" },
    { displayName: "Small", value: "small" },
    { displayName: "Main", value: "main" },
    { displayName: "Alt", value: "alt" },
    { displayName: "2", value: "2" },
];
const logoTab: TabDefinition = {
    displayName: "Logo",
    sortOrder: 2,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 3,
};

const logoTabFields: FoundationFieldDefinition[] = [
    {
        displayName: "Logo Width",
        ...logoWidthField,
        defaultValue: "9.75rem",
        tab: logoTab,
        isRequired: false,
    },
    {
        displayName: "Logo Height",
        ...logoHeightField,
        defaultValue: "9.75rem",
        tab: logoTab,
        isRequired: false,
    },
    {
        name: fields.logo,
        editorTemplate: "ImagePickerField",
        defaultValue: "",
        isRequired: false,
        sortOrder: 1,
        tab: logoTab,
        fieldType: "Contextual",
    },
    { displayName: "Left Padding", ...horizontalPaddingField, defaultValue: "", tab: logoTab },
    { displayName: "Top Padding", ...verticalPaddingField, defaultValue: "", tab: logoTab },
];

const imageTabFields: Bunzl.BunzlDefinition[] = [
    {
        name: fields.image,
        editorTemplate: "ImagePickerField",
        defaultValue: "",
        isRequired: false,
        sortOrder: 2,
        tab: imageTab,
        fieldType: "Contextual",
    },
    {
        name: fields.backgroundColor,
        editorTemplate: "CustomColorPickerField",
        defaultValue: "#fafafa",
        tab: imageTab,
        fieldType: "General",
    },
    {
        name: fields.objectFit,
        displayName: "Object Fit",
        editorTemplate: "DropDownField",
        defaultValue: "cover",
        options: [
            { displayName: "Contain", value: "contain" },
            { displayName: "Cover", value: "cover" },
            { displayName: "Fill", value: "fill" },
            { displayName: "None", value: "none" },
            { displayName: "Scale Down", value: "scale-down" },
            { displayName: "Manual", value: "manual" },
        ],
        tab: imageTab,
        fieldType: "Contextual",
    },
    {
        displayName: "Image Width",
        ...imageWidthField,
        defaultValue: "426px",
        tab: imageTab,
        isVisible: ({ fields }) => fields.objectFit === "manual",
    },
    {
        displayName: "Image Height",
        ...imageHeightField,
        defaultValue: "506px",
        tab: imageTab,
        isVisible: ({ fields }) => fields.objectFit === "manual",
    },
    {
        displayName: "Image Left Padding",
        ...imageHorizontalPaddingField,
        defaultValue: "",
        tab: imageTab,
        isVisible: ({ fields }) => fields.objectFit === "manual",
    },
    {
        displayName: "Image Top Padding",
        ...imageVerticalPaddingField,
        defaultValue: "",
        tab: imageTab,
        isVisible: ({ fields }) => fields.objectFit === "manual",
    },

    {
        name: fields.blendMode,
        displayName: "Blend Mode",
        editorTemplate: "DropDownField",
        defaultValue: "multiply",
        options: [
            { displayName: "Normal", value: "normal" },
            { displayName: "Multiply", value: "multiply" },
            { displayName: "Screen", value: "screen" },
            { displayName: "Overlay", value: "overlay" },
            { displayName: "Darken", value: "darken" },
            { displayName: "Lighten", value: "lighten" },
            { displayName: "Color Dodge", value: "color-dodge" },
            { displayName: "Color Burn", value: "color-burn" },
            { displayName: "Hard Light", value: "hard-light" },
            { displayName: "Soft Light", value: "soft-light" },
            { displayName: "Difference", value: "difference" },
            { displayName: "Exclusion", value: "exclusion" },
            { displayName: "Luminosity", value: "luminosity" },
        ],
        tab: imageTab,
        sortOrder: 5,
        fieldType: "Contextual",
    },
];

const basicTabFields: FoundationFieldDefinition[] = [
    {
        name: fields.link,
        editorTemplate: "LinkField",
        defaultValue: { value: "", type: "Url" },
        isRequired: false,
        fieldType: "Contextual",
        sortOrder: 0,
    },
    {
        name: fields.shadow,
        displayName: "Shadow",
        editorTemplate: "DropDownField",
        options: shadowOptions,
        defaultValue: "",
        fieldType: "Contextual",
    },

    {
        displayName: "Border Radius",
        ...radiusField,
        defaultValue: "10px",
        fieldType: "Contextual",
    },
    {
        displayName: "Body Width",
        ...bodyWidthField,
        defaultValue: "426px",
    },
    {
        displayName: "Body Height",
        ...bodyHeightField,
        defaultValue: "506px",
    },
];

const responsiveConfig: ResponsiveTabConfig[] = [
    {
        name: "Basic",
        tab: {
            displayName: "Basic",
            sortOrder: 0,
        },
        fields: basicTabFields,
    },
    {
        name: "Logo",
        tab: logoTab,
        fields: logoTabFields,
    },
    {
        name: "Image",
        tab: imageTab,
        fields: imageTabFields as FoundationFieldDefinition[],
    },
];

const responsiveFields = createResponsiveFieldDefinitions(responsiveConfig);
const widgetModule: WidgetModule<FoundationFieldDefinition> = {
    component: LinkContainer,
    definition: {
        group: "Common",
        icon: "LinkContainer",
        fieldDefinitions: [
            ...responsiveFields,
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (!value) return "";
                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }
                    return "Unable to parse CSS";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
